<template>
  <div class="org-page">
    <div class="filter-card">
      <div class="filter-card__body">
        <div class="filter-card__heading">
          <span class="filter-card__title">{{ $t('org.my.title') }}</span>
          <span class="filter-card__sub">{{ $t('org.my.tip') }}</span>
        </div>
        <span class="filter-card__actions">
          <a-tooltip :title="hasTopOrg ? $t('org.create.top.limit.tip') : ''" placement="bottom">
            <span class="org-create-wrap">
              <a-button type="primary" icon="plus" :disabled="hasTopOrg" @click="openCreate">{{ $t('org.create.button') }}</a-button>
            </span>
          </a-tooltip>
          <a-button icon="team" @click="openJoin">{{ $t('org.join.button') }}</a-button>
          <a-button icon="reload" @click="loadOrgs">{{ $t('org.refresh') }}</a-button>
          <a-button icon="file-text" :disabled="!isTopOrgCreator" @click="openLogs()">{{ $t('org.logs.button') }}</a-button>
        </span>
      </div>
    </div>

    <div class="table-card">
      <a-table
        :columns="columns"
        :data-source="orgs"
        :loading="loading"
        :pagination="false"
        :locale="locale"
        row-key="id"
        :key="tableKey"
        :indent-size="40"
        default-expand-all-rows>
        <span slot="admin_count" slot-scope="text, record">
          <a
            class="org-admin-count"
            :class="record.admin_count > 0 ? 'org-admin-count--active' : 'org-admin-count--zero'"
            @click="openAdmins(record)">{{ record.admin_count }}<a-icon type="right" class="org-admin-count__arrow" /></a>
        </span>
        <span slot="driver_count" slot-scope="text, record">
          <a
            class="org-driver-count"
            :class="record.driver_count > 0 ? 'org-driver-count--active' : 'org-driver-count--zero'"
            @click="openDrivers(record)">{{ record.driver_count }}<a-icon type="right" class="org-driver-count__arrow" /></a>
        </span>
        <div slot="action" slot-scope="text, record" class="action-cell">
          <a-button type="link" @click="openCreateSub(record)">{{ $t('org.create.sub.button') }}</a-button>
          <span class="action-divider"></span>
          <a-button type="link" @click="openAddAdmin(record)">{{ $t('org.add.admin.button') }}</a-button>
          <template v-if="record.is_owner">
            <span class="action-divider"></span>
            <a-button type="link" @click="openRename(record)">{{ $t('org.rename.button') }}</a-button>
            <span class="action-divider"></span>
            <a-button type="link" class="action-link--danger" @click="openDelete(record)">{{ $t('org.delete.button') }}</a-button>
          </template>
        </div>
      </a-table>
    </div>

    <!-- 修改组织名称 -->
    <a-modal
      v-model="renameVisible"
      :title="$t('org.rename.modal.title')"
      :confirm-loading="renameLoading"
      :mask-closable="false"
      @ok="handleRename">
      <a-input
        v-model="renameName"
        size="large"
        :placeholder="$t('org.rename.placeholder')"
        allow-clear
        @pressEnter="handleRename" />
    </a-modal>

    <!-- 添加管理员 -->
    <a-modal
      v-model="addAdminVisible"
      :title="$t('org.add.admin.modal.title')"
      :confirm-loading="addAdminLoading"
      :mask-closable="false"
      @ok="handleAddAdmin">
      <p class="org-add-admin-tip">{{ $t('org.add.admin.tip') }}</p>
      <a-form :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
        <a-form-item :label="$t('user.account')">
          <a-input v-model="addAdminAccount" :placeholder="$t('org.add.admin.account.placeholder')" allow-clear autocomplete="off" @pressEnter="handleAddAdmin" />
          <span v-if="adminAccountHint" class="account-hint" :class="'account-hint--' + adminAccountHint">{{ accountHintText(adminAccountHint) }}</span>
        </a-form-item>
        <a-form-item :label="$t('user.password')">
          <a-input-password v-model="addAdminPasswd" :placeholder="$t('org.add.admin.passwd.placeholder')" autocomplete="new-password" @pressEnter="handleAddAdmin" />
        </a-form-item>
        <a-form-item :label="$t('user.password.confirm')">
          <a-input-password v-model="addAdminPasswd2" :placeholder="$t('user.password.confirm.tip')" autocomplete="new-password" @pressEnter="handleAddAdmin" />
        </a-form-item>
        <a-form-item :label="$t('user.name')">
          <a-input v-model="addAdminName" :placeholder="$t('org.add.admin.name.placeholder')" allow-clear @pressEnter="handleAddAdmin" />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 管理员管理 -->
    <a-modal
      v-model="adminsVisible"
      :title="adminsOrg ? $t('org.admins.modal.title.with.org').replace('{name}', adminsOrg.name) : $t('org.admins.modal.title')"
      :footer="null"
      :mask-closable="false"
      :width="840">
      <div class="admins-toolbar">
        <span class="admins-toolbar__tip">{{ $t('org.admins.tip') }}</span>
        <a-button v-if="adminsOrg && adminsOrg.is_owner" type="primary" icon="user-add" size="small" @click="openAddAdmin(adminsOrg)">{{ $t('org.add.admin.button') }}</a-button>
      </div>
      <a-table
        :columns="adminColumns"
        :data-source="admins"
        :loading="adminsLoading"
        :pagination="false"
        :locale="locale"
        row-key="uid"
        size="small">
        <span slot="status" slot-scope="text, record">
          <a-switch
            :checked="record.status === 1"
            :disabled="!(adminsOrg && adminsOrg.is_owner) || record.is_creator"
            @change="checked => toggleAdminStatus(record, checked)" />
        </span>
        <div slot="action" slot-scope="text, record" class="action-cell">
          <template v-if="adminsOrg && adminsOrg.is_owner && !record.is_creator">
            <a-button type="link" @click="openChangeOrg(record)">{{ $t('org.admins.change.org') }}</a-button>
            <span class="action-divider"></span>
            <a-button type="link" @click="openAdminRename(record)">{{ $t('org.admins.rename') }}</a-button>
            <span class="action-divider"></span>
            <a-button type="link" @click="openAdminResetPwd(record)">{{ $t('org.admins.reset.pwd') }}</a-button>
            <span class="action-divider"></span>
            <a-button type="link" class="action-link--danger" @click="handleDeleteAdmin(record)">{{ $t('org.admins.delete') }}</a-button>
          </template>
          <span v-if="record.is_creator" class="admins-creator">{{ $t('org.admins.creator') }}</span>
        </div>
      </a-table>
    </a-modal>

    <!-- 司机列表 -->
    <a-modal
      v-model="driversVisible"
      :title="driversOrg ? $t('org.drivers.modal.title.with.org').replace('{name}', driversOrg.name) : $t('org.drivers.modal.title')"
      :footer="null"
      :mask-closable="false"
      :width="840">
      <div class="drivers-toolbar">
        <span class="drivers-toolbar__tip">{{ $t('org.drivers.selected').replace('{count}', driverSelection.length) }}</span>
        <span class="drivers-toolbar__actions">
          <a-button type="primary" icon="user-add" size="small" @click="openAddDriver">{{ $t('org.drivers.add') }}</a-button>
          <a-button type="link" size="small" :disabled="driverSelection.length === 0" @click="openDriverChangeOrg">{{ $t('org.drivers.change.org') }}</a-button>
          <a-button type="link" size="small" class="org-action--disable" :disabled="driverSelection.length === 0" @click="toggleSelectedDrivers(0)">{{ $t('org.drivers.disable') }}</a-button>
          <a-button type="link" size="small" :disabled="driverSelection.length === 0" @click="toggleSelectedDrivers(1)">{{ $t('org.drivers.enable') }}</a-button>
          <a-button type="link" size="small" class="org-action--disable" :disabled="driverSelection.length === 0" @click="deleteSelectedDrivers">{{ $t('org.drivers.delete') }}</a-button>
        </span>
      </div>
      <a-table
        :columns="driverColumns"
        :data-source="drivers"
        :loading="driversLoading"
        :pagination="false"
        :locale="locale"
        row-key="user_id"
        size="small"
        :row-selection="{ selectedRowKeys: driverSelection, onChange: onDriverSelectChange }">
        <span slot="driver_status" slot-scope="text, record">
          <a-switch
            :checked="record.status === 1"
            :disabled="!(driversOrg && driversOrg.is_owner)"
            @change="checked => toggleDriverStatus(record, checked)" />
        </span>
        <div slot="driver_action" slot-scope="text, record" class="action-cell">
          <a-button type="link" @click="openDriverRename(record)">{{ $t('org.drivers.rename') }}</a-button>
        </div>
      </a-table>
    </a-modal>

    <!-- 添加司机 -->
    <a-modal
      v-model="addDriverVisible"
      :title="$t('org.drivers.add.modal.title')"
      :confirm-loading="addDriverLoading"
      :mask-closable="false"
      @ok="handleAddDriver">
      <a-form :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
        <a-form-item :label="$t('user.account')">
          <a-input v-model="addDriverAccount" :placeholder="$t('org.drivers.add.account.placeholder')" allow-clear autocomplete="off" @pressEnter="handleAddDriver" />
          <span v-if="driverAccountHint" class="account-hint" :class="'account-hint--' + driverAccountHint">{{ accountHintText(driverAccountHint) }}</span>
        </a-form-item>
        <a-form-item :label="$t('user.password')">
          <a-input-password v-model="addDriverPasswd" :placeholder="$t('org.drivers.add.passwd.placeholder')" autocomplete="new-password" @pressEnter="handleAddDriver" />
        </a-form-item>
        <a-form-item :label="$t('user.password.confirm')">
          <a-input-password v-model="addDriverPasswd2" :placeholder="$t('user.password.confirm.tip')" autocomplete="new-password" @pressEnter="handleAddDriver" />
        </a-form-item>
        <a-form-item :label="$t('user.name')">
          <a-input v-model="addDriverName" :placeholder="$t('org.drivers.add.name.placeholder')" allow-clear @pressEnter="handleAddDriver" />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 修改司机姓名 -->
    <a-modal
      v-model="driverRenameVisible"
      :title="$t('org.drivers.rename.modal.title')"
      :confirm-loading="driverRenameLoading"
      :mask-closable="false"
      @ok="handleDriverRename">
      <a-input v-model="driverRenameName" size="large" :placeholder="$t('org.drivers.rename.placeholder')" allow-clear @pressEnter="handleDriverRename" />
    </a-modal>

    <!-- 司机移入其他组织 -->
    <a-modal
      v-model="driverChangeOrgVisible"
      :title="$t('org.drivers.change.org.modal.title')"
      :confirm-loading="driverChangeOrgLoading"
      :mask-closable="false"
      @ok="handleDriverChangeOrg">
      <a-select
        v-model="driverChangeOrgTarget"
        size="large"
        style="width: 100%"
        :placeholder="$t('org.drivers.change.org.placeholder')">
        <a-select-option v-for="org in driverChangeOrgOptions" :key="org.id" :value="org.id">
          {{ org.name }}
        </a-select-option>
      </a-select>
    </a-modal>

    <!-- 重置管理员密码 -->
    <a-modal
      v-model="adminResetPwdVisible"
      :title="$t('org.admins.reset.pwd.modal.title')"
      :confirm-loading="adminResetPwdLoading"
      :mask-closable="false"
      @ok="handleAdminResetPwd">
      <a-form :label-col="{ span: 4 }" :wrapper-col="{ span: 20 }">
        <a-form-item :label="$t('user.password')">
          <a-input-password v-model="adminResetPwdPasswd" :placeholder="$t('org.admins.reset.pwd.placeholder')" autocomplete="new-password" @pressEnter="handleAdminResetPwd" />
        </a-form-item>
        <a-form-item :label="$t('user.password.confirm')">
          <a-input-password v-model="adminResetPwdPasswd2" :placeholder="$t('user.password.confirm.tip')" autocomplete="new-password" @pressEnter="handleAdminResetPwd" />
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 修改管理员姓名 -->
    <a-modal
      v-model="adminRenameVisible"
      :title="$t('org.admins.rename.modal.title')"
      :confirm-loading="adminRenameLoading"
      :mask-closable="false"
      @ok="handleAdminRename">
      <a-input v-model="adminRenameName" size="large" :placeholder="$t('org.admins.rename.placeholder')" allow-clear @pressEnter="handleAdminRename" />
    </a-modal>

    <!-- 修改管理员所在组织 -->
    <a-modal
      v-model="changeOrgVisible"
      :title="$t('org.admins.change.org.modal.title')"
      :confirm-loading="changeOrgLoading"
      :mask-closable="false"
      @ok="handleChangeOrg">
      <a-select
        v-model="changeOrgTarget"
        size="large"
        style="width: 100%"
        :placeholder="$t('org.admins.change.org.placeholder')">
        <a-select-option v-for="org in changeOrgOptions" :key="org.id" :value="org.id">
          {{ org.name }}
        </a-select-option>
      </a-select>
    </a-modal>

    <!-- 删除组织: 输入组织名称确认 -->
    <a-modal
      v-model="deleteVisible"
      :title="$t('org.delete.modal.title')"
      :confirm-loading="deleteLoading"
      :mask-closable="false"
      :ok-button-props="{ disabled: !deleteConfirmOk }"
      @ok="handleDelete">
      <p class="org-delete-tip">
        {{ $t('org.delete.confirm.prefix') }}
        <span class="org-delete-name">{{ deleteOrg ? deleteOrg.name : '' }}</span>
        {{ $t('org.delete.confirm.suffix') }}
      </p>
      <a-input
        v-model="deleteName"
        size="large"
        :placeholder="$t('org.delete.confirm.placeholder')"
        allow-clear
        @pressEnter="handleDelete" />
    </a-modal>

    <!-- 创建组织 / 创建下级组织 -->
    <a-modal
      v-model="createVisible"
      :title="createParentOrg ? $t('org.create.sub.modal.title').replace('{name}', createParentOrg.name) : $t('org.create.modal.title')"
      :confirm-loading="createLoading"
      :mask-closable="false"
      @ok="handleCreate">
      <a-input
        v-model="createName"
        size="large"
        :placeholder="createParentId ? $t('org.create.sub.placeholder') : $t('org.create.placeholder')"
        allow-clear
        @pressEnter="handleCreate" />
    </a-modal>

    <!-- 加入组织 -->
    <a-modal
      v-model="joinVisible"
      :title="$t('org.join.modal.title')"
      :confirm-loading="joinLoading"
      :mask-closable="false"
      @ok="handleJoin">
      <a-input
        v-model="inviteCode"
        size="large"
        :placeholder="$t('org.join.placeholder')"
        allow-clear
        @pressEnter="handleJoin" />
      <p v-if="joinOrgLoading" class="org-join-preview org-join-preview--muted">{{ $t('org.join.searching') }}</p>
      <p v-else-if="joinOrgPreview" class="org-join-preview">{{ $t('org.join.org.name').replace('{name}', joinOrgPreview.name) }}</p>
      <p v-else-if="inviteCode && inviteCode.trim()" class="org-join-preview org-join-preview--error">{{ $t('org.join.not.found') }}</p>
    </a-modal>

    <!-- 操作记录 -->
    <a-modal
      v-model="logsVisible"
      :title="$t('org.logs.modal.title')"
      :footer="null"
      :mask-closable="false"
      :width="760">
      <a-table
        :columns="logColumns"
        :data-source="logs"
        :loading="logsLoading"
        :pagination="logsPagination"
        :locale="locale"
        row-key="id"
        size="small"
        @change="handleLogsTableChange">
        <span slot="action_label" slot-scope="text">{{ getActionLabel(text) }}</span>
      </a-table>
    </a-modal>
  </div>
</template>

<script>
import md5 from 'md5'
import { changeOrganizationAdminOrg, changeOrganizationDriverOrg, checkOrganizationAccount, createOrganization, createOrganizationAdmin, createOrganizationDriver, deleteOrganization, deleteOrganizationAdmin, deleteOrganizationDrivers, joinOrganization, loadMyOrganization, loadOrganizationAdmins, loadOrganizationByInviteCode, loadOrganizationDrivers, loadOrganizationOperationLogs, resetOrganizationAdminPassword, toggleOrganizationAdminStatus, toggleOrganizationDriverStatus, updateOrganizationAdminName, updateOrganizationDriverName, updateOrganizationName } from '@/api/data'
import { getI18nLanguage } from '@/locales'

export default {
  name: 'Organization',
  data () {
    return {
      loading: false,
      orgs: [],
      tableKey: 0,
      hasTopOrg: false,
      createVisible: false,
      createLoading: false,
      createName: '',
      createParentId: null,
      createParentOrg: null,
      joinVisible: false,
      joinLoading: false,
      inviteCode: '',
      joinOrgPreview: null,
      joinOrgLoading: false,
      lookupTimer: null,
      deleteVisible: false,
      deleteLoading: false,
      deleteOrg: null,
      deleteName: '',
      renameVisible: false,
      renameLoading: false,
      renameOrg: null,
      renameName: '',
      addAdminVisible: false,
      addAdminOrg: null,
      addAdminLoading: false,
      addAdminAccount: '',
      addAdminPasswd: '',
      addAdminPasswd2: '',
      addAdminName: '',
      adminsVisible: false,
      adminsOrg: null,
      adminsLoading: false,
      admins: [],
      driversVisible: false,
      driversOrg: null,
      driversLoading: false,
      drivers: [],
      driverSelection: [],
      driverRenameVisible: false,
      driverRenameLoading: false,
      driverRenameUid: null,
      driverRenameName: '',
      driverChangeOrgVisible: false,
      driverChangeOrgLoading: false,
      driverChangeOrgTarget: undefined,
      addDriverVisible: false,
      addDriverLoading: false,
      addDriverAccount: '',
      addDriverPasswd: '',
      addDriverPasswd2: '',
      addDriverName: '',
      // 账户名实时检测状态: '' | 'checking' | 'available' | 'taken' | 'invalid' | 'email'
      adminAccountHint: '',
      driverAccountHint: '',
      adminCheckTimer: null,
      driverCheckTimer: null,
      driverColumns: [{
        title: this.$t('user.id'),
        dataIndex: 'user_id',
        key: 'user_id',
        align: 'center'
      }, {
        title: this.$t('user.name'),
        dataIndex: 'name',
        key: 'name',
        align: 'center'
      }, {
        title: this.$t('user.account'),
        dataIndex: 'account',
        key: 'account',
        align: 'center'
      }, {
        title: this.$t('org.admins.status'),
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        scopedSlots: { customRender: 'driver_status' }
      }, {
        title: this.$t('org.drivers.bind.time'),
        dataIndex: 'create_time',
        key: 'create_time',
        align: 'center'
      }, {
        title: this.$t('org.action'),
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        scopedSlots: { customRender: 'driver_action' }
      }],
      adminRenameVisible: false,
      adminRenameLoading: false,
      adminRenameUid: null,
      adminRenameName: '',
      adminResetPwdVisible: false,
      adminResetPwdLoading: false,
      adminResetPwdUid: null,
      adminResetPwdPasswd: '',
      adminResetPwdPasswd2: '',
      changeOrgVisible: false,
      changeOrgLoading: false,
      changeOrgAdminUid: null,
      changeOrgTarget: undefined,
      logsVisible: false,
      logsLoading: false,
      logs: [],
      logsPageNo: 1,
      logsPageSize: 20,
      logsTotal: 0,
      logColumns: [{
        title: this.$t('org.logs.time'),
        dataIndex: 'create_time',
        key: 'create_time',
        align: 'center',
        width: 150
      }, {
        title: this.$t('org.logs.org'),
        dataIndex: 'org_name',
        key: 'org_name',
        align: 'center'
      }, {
        title: this.$t('org.logs.operator'),
        dataIndex: 'operator_name',
        key: 'operator_name',
        align: 'center'
      }, {
        title: this.$t('org.logs.action'),
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        scopedSlots: { customRender: 'action_label' }
      }, {
        title: this.$t('org.logs.target'),
        dataIndex: 'target_name',
        key: 'target_name',
        align: 'center'
      }],
      allOrgs: [],
      adminColumns: [{
        title: this.$t('user.name'),
        dataIndex: 'name',
        key: 'name',
        align: 'center'
      }, {
        title: this.$t('user.account'),
        dataIndex: 'account',
        key: 'account',
        align: 'center'
      }, {
        title: this.$t('org.admins.status'),
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        scopedSlots: { customRender: 'status' }
      }, {
        title: this.$t('org.action'),
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        scopedSlots: { customRender: 'action' }
      }],
      locale: {
        emptyText: this.$t('org.empty.tip')
      },
      columns: [{
        title: this.$t('org.name'),
        dataIndex: 'name',
        key: 'name',
        align: 'left',
        width: 240
      }, {
        title: this.$t('org.admin.count'),
        dataIndex: 'admin_count',
        key: 'admin_count',
        width: 100,
        align: 'center',
        scopedSlots: { customRender: 'admin_count' }
      }, {
        title: this.$t('org.driver.count'),
        dataIndex: 'driver_count',
        key: 'driver_count',
        width: 100,
        align: 'center',
        scopedSlots: { customRender: 'driver_count' }
      }, {
        title: this.$t('org.create.time'),
        dataIndex: 'create_time',
        key: 'create_time',
        align: 'center'
      }, {
        title: this.$t('org.action'),
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        width: 280,
        scopedSlots: { customRender: 'action' }
      }]
    }
  },
  watch: {
    inviteCode (val) {
      this.lookupInviteOrg(val)
    },
    addAdminAccount () {
      this.onAdminAccountInput()
    },
    addDriverAccount () {
      this.onDriverAccountInput()
    }
  },
  computed: {
    deleteConfirmOk () {
      return !!(this.deleteName && this.deleteOrg && this.deleteName.trim() === this.deleteOrg.name)
    },
    changeOrgOptions () {
      return this.allOrgs.filter(o => o.id !== (this.adminsOrg && this.adminsOrg.id))
    },
    driverChangeOrgOptions () {
      return this.allOrgs.filter(o => o.id !== (this.driversOrg && this.driversOrg.id))
    },
    ownedOrgs () {
      return this.allOrgs.filter(o => o.is_owner)
    },
    isTopOrgCreator () {
      return this.allOrgs.some(o => o.is_owner && Number(o.parent_id) === 0)
    },
    logsPagination () {
      return {
        current: this.logsPageNo,
        pageSize: this.logsPageSize,
        total: this.logsTotal,
        showSizeChanger: false,
        size: 'small'
      }
    }
  },
  created () {
    this.loadOrgs()
  },
  methods: {
    getErrorMessage (msg) {
      if (typeof msg === 'string') {
        return msg
      }
      const lang = getI18nLanguage()
      const langKey = lang.replace('-', '').toLowerCase()
      if (langKey.includes('zh')) {
        return msg['zh'] || msg['zh-CN'] || msg['en'] || msg['en-US'] || ''
      } else if (langKey.includes('ja')) {
        return msg['ja'] || msg['ja-JP'] || msg['en'] || msg['en-US'] || ''
      }
      return msg['en'] || msg['en-US'] || ''
    },
    loadOrgs () {
      this.loading = true
      loadMyOrganization({}).then(res => {
        if (res.code === 0) {
          const list = (res.result && res.result.orgs) || []
          this.allOrgs = list
          this.hasTopOrg = list.some(o => Number(o.parent_id) === 0)
          this.orgs = this.buildTree(list)
          this.tableKey++
        }
      }).finally(() => {
        this.loading = false
      })
    },
    buildTree (orgs) {
      const map = {}
      orgs.forEach(o => {
        o.children = []
        map[o.id] = o
      })
      const roots = []
      orgs.forEach(o => {
        if (o.parent_id && map[o.parent_id]) {
          map[o.parent_id].children.push(o)
        } else {
          roots.push(o)
        }
      })
      return roots
    },
    openCreate () {
      this.createName = ''
      this.createParentId = null
      this.createParentOrg = null
      this.createVisible = true
    },
    openCreateSub (record) {
      this.createName = ''
      this.createParentId = record.id
      this.createParentOrg = record
      this.createVisible = true
    },
    handleCreate () {
      const name = (this.createName || '').trim()
      if (!name) {
        this.$message.warning(this.$t('org.create.name.required'))
        return
      }
      this.createLoading = true
      createOrganization({ name, parent_id: this.createParentId }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.create.success'))
          this.createVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.create.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.create.fail'))
      }).finally(() => {
        this.createLoading = false
      })
    },
    openJoin () {
      this.inviteCode = ''
      this.joinVisible = true
    },
    lookupInviteOrg (val) {
      clearTimeout(this.lookupTimer)
      const code = (val || '').trim()
      this.joinOrgPreview = null
      this.joinOrgLoading = false
      if (!code) {
        return
      }
      this.joinOrgLoading = true
      this.lookupTimer = setTimeout(() => {
        loadOrganizationByInviteCode({ invite_code: code }).then(res => {
          this.joinOrgLoading = false
          if (res.code === 0 && res.result && res.result.org) {
            this.joinOrgPreview = res.result.org
          } else {
            this.joinOrgPreview = null
          }
        }).catch(() => {
          this.joinOrgLoading = false
          this.joinOrgPreview = null
        })
      }, 400)
    },
    handleJoin () {
      const code = (this.inviteCode || '').trim()
      if (!code) {
        this.$message.warning(this.$t('org.join.code.required'))
        return
      }
      this.joinLoading = true
      joinOrganization({ invite_code: code }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.join.success'))
          this.joinVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.join.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.join.fail'))
      }).finally(() => {
        this.joinLoading = false
      })
    },
    openRename (record) {
      this.renameOrg = record
      this.renameName = record.name
      this.renameVisible = true
    },
    handleRename () {
      const name = (this.renameName || '').trim()
      if (!name) {
        this.$message.warning(this.$t('org.rename.name.required'))
        return
      }
      this.renameLoading = true
      updateOrganizationName({ org_id: this.renameOrg.id, name }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.rename.success'))
          this.renameVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.rename.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.rename.fail'))
      }).finally(() => {
        this.renameLoading = false
      })
    },
    openAddAdmin (record) {
      this.addAdminOrg = record
      this.addAdminAccount = ''
      this.addAdminPasswd = ''
      this.addAdminPasswd2 = ''
      this.addAdminName = ''
      this.adminAccountHint = ''
      clearTimeout(this.adminCheckTimer)
      this.addAdminVisible = true
    },
    handleAddAdmin () {
      const account = (this.addAdminAccount || '').trim()
      const passwd = this.addAdminPasswd || ''
      if (!account) {
        this.$message.warning(this.$t('org.add.admin.account.required'))
        return
      }
      if (!/^[\w.+-]+@[\w-]+(\.[\w-]+)+$/.test(account)) {
        this.$message.warning(this.$t('org.add.admin.account.format'))
        return
      }
      if (!passwd || passwd.length < 6) {
        this.$message.warning(this.$t('org.add.admin.passwd.min'))
        return
      }
      if (passwd !== (this.addAdminPasswd2 || '')) {
        this.$message.warning(this.$t('user.password.confirm.error'))
        return
      }
      this.addAdminLoading = true
      createOrganizationAdmin({
        org_id: this.addAdminOrg.id,
        account,
        passwd: md5(passwd),
        name: (this.addAdminName || '').trim()
      }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.add.admin.success'))
          this.addAdminVisible = false
          this.loadOrgs()
          if (this.adminsVisible) {
            this.loadAdmins()
          }
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.add.admin.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.add.admin.fail'))
      }).finally(() => {
        this.addAdminLoading = false
      })
    },
    openAdmins (record) {
      this.adminsOrg = record
      this.adminsVisible = true
      this.loadAdmins()
    },
    handleDeleteAdmin (record) {
      const self = this
      this.$confirm({
        title: this.$t('org.admins.delete.confirm'),
        okType: 'danger',
        onOk () {
          return deleteOrganizationAdmin({ org_id: self.adminsOrg.id, uid: record.uid }).then(res => {
            if (res.code === 0) {
              self.$message.success(self.$t('org.admins.delete.success'))
              self.loadAdmins()
              self.loadOrgs()
            } else {
              self.$message.error(self.getErrorMessage(res.msg) || self.$t('org.admins.delete.fail'))
            }
          }).catch(() => {
            self.$message.error(self.$t('org.admins.delete.fail'))
          })
        }
      })
    },
    openDrivers (record) {
      this.driversOrg = record
      this.driversVisible = true
      this.loadDrivers()
    },
    loadDrivers () {
      if (!this.driversOrg) {
        return
      }
      this.driversLoading = true
      loadOrganizationDrivers({ org_id: this.driversOrg.id }).then(res => {
        if (res.code === 0) {
          this.drivers = (res.result && res.result.drivers) || []
          this.driverSelection = []
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.drivers.load.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.drivers.load.fail'))
      }).finally(() => {
        this.driversLoading = false
      })
    },
    onDriverSelectChange (selectedRowKeys) {
      this.driverSelection = selectedRowKeys
    },
    openAddDriver () {
      this.addDriverAccount = ''
      this.addDriverPasswd = ''
      this.addDriverPasswd2 = ''
      this.addDriverName = ''
      this.driverAccountHint = ''
      clearTimeout(this.driverCheckTimer)
      this.addDriverVisible = true
    },
    handleAddDriver () {
      const account = (this.addDriverAccount || '').trim()
      if (!account) {
        this.$message.warning(this.$t('org.drivers.add.account.required'))
        return
      }
      const passwd = this.addDriverPasswd || ''
      if (!passwd || passwd.length < 6) {
        this.$message.warning(this.$t('org.drivers.add.passwd.min'))
        return
      }
      if (passwd !== (this.addDriverPasswd2 || '')) {
        this.$message.warning(this.$t('user.password.confirm.error'))
        return
      }
      this.addDriverLoading = true
      createOrganizationDriver({ org_id: this.driversOrg.id, account, passwd, name: (this.addDriverName || '').trim() }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.drivers.add.success'))
          this.addDriverVisible = false
          this.loadDrivers()
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.drivers.add.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.drivers.add.fail'))
      }).finally(() => {
        this.addDriverLoading = false
      })
    },
    onDriverAccountInput () {
      clearTimeout(this.driverCheckTimer)
      const account = (this.addDriverAccount || '').trim()
      if (!account) {
        this.driverAccountHint = ''
        return
      }
      this.driverAccountHint = 'checking'
      this.driverCheckTimer = setTimeout(() => this.doCheckAccount('driver', account), 400)
    },
    onAdminAccountInput () {
      clearTimeout(this.adminCheckTimer)
      const account = (this.addAdminAccount || '').trim()
      if (!account) {
        this.adminAccountHint = ''
        return
      }
      this.adminAccountHint = 'checking'
      this.adminCheckTimer = setTimeout(() => this.doCheckAccount('admin', account), 400)
    },
    doCheckAccount (type, account) {
      // 丢弃过期响应: 仅当输入框当前值仍等于发起检测的 account 时才回写
      const current = (type === 'admin' ? this.addAdminAccount : this.addDriverAccount || '').trim()
      if (current !== account) {
        return
      }
      const field = type === 'admin' ? 'adminAccountHint' : 'driverAccountHint'
      checkOrganizationAccount({ account, type }).then(res => {
        if ((type === 'admin' ? this.addAdminAccount : this.addDriverAccount || '').trim() !== account) {
          return
        }
        if (res.code === 0 && res.result) {
          this[field] = res.result.available ? 'available' : (res.result.reason || 'taken')
        } else {
          this[field] = ''
        }
      }).catch(() => {
        this[field] = ''
      })
    },
    accountHintText (hint) {
      if (!hint) {
        return ''
      }
      if (hint === 'available') {
        return this.$t('org.account.available')
      }
      if (hint === 'checking') {
        return this.$t('org.account.checking')
      }
      return this.$t('org.account.' + hint)
    },
    openDriverRename (record) {
      this.driverRenameUid = record.user_id
      this.driverRenameName = record.name
      this.driverRenameVisible = true
    },
    handleDriverRename () {
      const name = (this.driverRenameName || '').trim()
      if (!name) {
        this.$message.warning(this.$t('org.drivers.rename.name.required'))
        return
      }
      this.driverRenameLoading = true
      updateOrganizationDriverName({ org_id: this.driversOrg.id, user_id: this.driverRenameUid, name }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.drivers.rename.success'))
          this.driverRenameVisible = false
          this.loadDrivers()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.drivers.rename.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.drivers.rename.fail'))
      }).finally(() => {
        this.driverRenameLoading = false
      })
    },
    deleteSelectedDrivers () {
      if (this.driverSelection.length === 0) {
        return
      }
      const ids = this.driverSelection.slice()
      const self = this
      this.$confirm({
        title: this.$t('org.drivers.delete.confirm'),
        onOk () {
          return deleteOrganizationDrivers({ org_id: self.driversOrg.id, user_ids: ids }).then(res => {
            if (res.code === 0) {
              self.$message.success(self.$t('org.drivers.delete.success'))
              self.driverSelection = []
              self.loadDrivers()
              self.loadOrgs()
            } else {
              self.$message.error(self.getErrorMessage(res.msg) || self.$t('org.drivers.delete.fail'))
            }
          }).catch(() => {
            self.$message.error(self.$t('org.drivers.delete.fail'))
          })
        }
      })
    },
    toggleDriverStatus (record, checked) {
      this.doToggleDrivers([record.user_id], checked ? 1 : 0)
    },
    toggleSelectedDrivers (status) {
      if (this.driverSelection.length === 0) {
        return
      }
      if (status === 0) {
        const ids = this.driverSelection.slice()
        const self = this
        this.$confirm({
          title: this.$t('org.drivers.disable.confirm'),
          onOk: () => self.doToggleDrivers(ids, 0)
        })
      } else {
        this.doToggleDrivers(this.driverSelection.slice(), 1)
      }
    },
    doToggleDrivers (ids, status) {
      return toggleOrganizationDriverStatus({ org_id: this.driversOrg.id, user_ids: ids, status: status }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.drivers.status.success'))
          this.loadDrivers()
          // 树上司机数只统计 status=1 的绑定，禁用/启用同样需要刷新
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.drivers.status.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.drivers.status.fail'))
      })
    },
    openDriverChangeOrg () {
      this.driverChangeOrgTarget = undefined
      this.driverChangeOrgVisible = true
    },
    handleDriverChangeOrg () {
      if (!this.driverChangeOrgTarget) {
        this.$message.warning(this.$t('org.drivers.change.org.required'))
        return
      }
      this.driverChangeOrgLoading = true
      changeOrganizationDriverOrg({
        org_id: this.driversOrg.id,
        target_org_id: this.driverChangeOrgTarget,
        user_ids: this.driverSelection
      }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.drivers.change.org.success'))
          this.driverChangeOrgVisible = false
          this.loadDrivers()
          this.loadOrgs()
        } else if (res.code === -16) {
          // 目标组织暂无管理员: 引导用户去该组织创建管理员
          this.driverChangeOrgVisible = false
          const targetOrg = this.allOrgs.find(o => o.id === this.driverChangeOrgTarget)
          const orgName = targetOrg ? targetOrg.name : ''
          const self = this
          this.$confirm({
            title: self.$t('org.drivers.change.org.noAdmin.title'),
            content: self.$t('org.drivers.change.org.noAdmin.content').replace('{name}', orgName),
            okText: self.$t('org.drivers.change.org.noAdmin.ok'),
            cancelText: self.$t('org.drivers.change.org.noAdmin.cancel'),
            onOk () {
              if (targetOrg) {
                self.openAdmins(targetOrg)
              }
            }
          })
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.drivers.change.org.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.drivers.change.org.fail'))
      }).finally(() => {
        this.driverChangeOrgLoading = false
      })
    },
    openLogs () {
      this.logsPageNo = 1
      this.logs = []
      this.logsVisible = true
      this.loadLogs()
    },
    handleLogsTableChange (pagination) {
      this.logsPageNo = pagination.current
      this.loadLogs()
    },
    loadLogs () {
      this.logsLoading = true
      loadOrganizationOperationLogs({ pageNo: this.logsPageNo, pageSize: this.logsPageSize }).then(res => {
        if (res.code === 0) {
          this.logs = res.result.logs || []
          this.logsTotal = res.result.totalCount || 0
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.logs.load.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.logs.load.fail'))
      }).finally(() => {
        this.logsLoading = false
      })
    },
    getActionLabel (action) {
      return this.$t('org.logs.action.' + action) || action
    },
    loadAdmins () {
      if (!this.adminsOrg) {
        return
      }
      this.adminsLoading = true
      loadOrganizationAdmins({ org_id: this.adminsOrg.id }).then(res => {
        if (res.code === 0) {
          this.admins = (res.result && res.result.admins) || []
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.admins.load.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.admins.load.fail'))
      }).finally(() => {
        this.adminsLoading = false
      })
    },
    toggleAdminStatus (record, checked) {
      toggleOrganizationAdminStatus({ org_id: this.adminsOrg.id, uid: record.uid, status: checked ? 1 : 0 }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.admins.status.success'))
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.admins.status.fail'))
        }
        this.loadAdmins()
      }).catch(() => {
        this.$message.error(this.$t('org.admins.status.fail'))
        this.loadAdmins()
      })
    },
    openAdminResetPwd (record) {
      this.adminResetPwdUid = record.uid
      this.adminResetPwdPasswd = ''
      this.adminResetPwdPasswd2 = ''
      this.adminResetPwdVisible = true
    },
    handleAdminResetPwd () {
      const passwd = this.adminResetPwdPasswd || ''
      if (!passwd || passwd.length < 6) {
        this.$message.warning(this.$t('org.admins.reset.pwd.min'))
        return
      }
      if (passwd !== (this.adminResetPwdPasswd2 || '')) {
        this.$message.warning(this.$t('user.password.confirm.error'))
        return
      }
      this.adminResetPwdLoading = true
      resetOrganizationAdminPassword({ org_id: this.adminsOrg.id, uid: this.adminResetPwdUid, passwd: md5(passwd) }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.admins.reset.pwd.success'))
          this.adminResetPwdVisible = false
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.admins.reset.pwd.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.admins.reset.pwd.fail'))
      }).finally(() => {
        this.adminResetPwdLoading = false
      })
    },
    openAdminRename (record) {
      this.adminRenameUid = record.uid
      this.adminRenameName = record.name
      this.adminRenameVisible = true
    },
    handleAdminRename () {
      const name = (this.adminRenameName || '').trim()
      if (!name) {
        this.$message.warning(this.$t('org.admins.rename.name.required'))
        return
      }
      this.adminRenameLoading = true
      updateOrganizationAdminName({ org_id: this.adminsOrg.id, uid: this.adminRenameUid, name }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.admins.rename.success'))
          this.adminRenameVisible = false
          this.loadAdmins()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.admins.rename.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.admins.rename.fail'))
      }).finally(() => {
        this.adminRenameLoading = false
      })
    },
    openChangeOrg (record) {
      this.changeOrgAdminUid = record.uid
      this.changeOrgTarget = undefined
      this.changeOrgVisible = true
    },
    handleChangeOrg () {
      if (!this.changeOrgTarget) {
        this.$message.warning(this.$t('org.admins.change.org.required'))
        return
      }
      this.changeOrgLoading = true
      changeOrganizationAdminOrg({
        org_id: this.adminsOrg.id,
        uid: this.changeOrgAdminUid,
        target_org_id: this.changeOrgTarget
      }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.admins.change.org.success'))
          this.changeOrgVisible = false
          this.loadAdmins()
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.admins.change.org.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.admins.change.org.fail'))
      }).finally(() => {
        this.changeOrgLoading = false
      })
    },
    openDelete (record) {
      this.deleteOrg = record
      this.deleteName = ''
      this.deleteVisible = true
    },
    handleDelete () {
      if (!this.deleteConfirmOk) {
        this.$message.warning(this.$t('org.delete.name.not.match'))
        return
      }
      this.deleteLoading = true
      deleteOrganization({ org_id: this.deleteOrg.id }).then(res => {
        if (res.code === 0) {
          this.$message.success(this.$t('org.delete.success'))
          this.deleteVisible = false
          this.loadOrgs()
        } else {
          this.$message.error(this.getErrorMessage(res.msg) || this.$t('org.delete.fail'))
        }
      }).catch(() => {
        this.$message.error(this.$t('org.delete.fail'))
      }).finally(() => {
        this.deleteLoading = false
      })
    }
  }
}
</script>

<style scoped lang="less">
// 列表页骨架（卡片底色、圆角、投影、卡片节奏），见 assets/list-page.less
@import '../../assets/list-page.less';

@primary: #0A76C6;
@radius: 6px;
@border-color: #dce5ee;
@text-primary: #101b25;
@shadow-card: 0 1px 2px rgba(16, 32, 48, 0.06), 0 1px 3px rgba(16, 32, 48, 0.04);

.org-page {
  margin: 0 auto;

  /deep/ .ant-table-tbody > tr > td:last-child .ant-btn-link {
    padding-left: 4px;
    padding-right: 4px;
  }
}

// 这一页顶上的卡不是筛选条，是组织列表的动作栏：
// 拿骨架的卡面，但内边距自己定（骨架那套是给堆叠字段用的）
.filter-card {
  padding: 16px 24px;

  &__body {
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__title {
    font-size: 16px;
    font-weight: 600;
    color: @text-primary;
  }

  &__heading {
    display: flex;
    align-items: baseline;
    gap: 10px;
    flex-wrap: wrap;
  }

  &__sub {
    font-size: 12px;
    color: var(--ink-2);
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}

// 创建组织按钮禁用时, 包一层 span 供 a-tooltip 捕获悬停(禁用态按钮自身不响应鼠标事件)
.org-create-wrap {
  display: inline-block;
}

// 工具条里的禁用（红色警示）；表格操作列里的删除走骨架的 .action-link--danger
.org-action--disable {
  color: var(--fail-ink);

  &:hover,
  &:focus {
    color: var(--fail);
  }
}

.org-admin-count,
.org-driver-count {
  display: inline-flex;
  align-items: center;
  font-weight: 600;

  &__arrow {
    margin-left: 3px;
    font-size: 8px;
    line-height: 1;
  }

  &--active {
    min-width: 28px;
    padding: 1px 10px;
    border-radius: 12px;
    color: #fff;
    background: @primary;
    text-align: center;
    transition: background 0.2s;

    &:hover {
      background: darken(@primary, 8%);
    }
  }

  &--zero {
    color: rgba(16, 27, 37, 0.45);

    &:hover {
      color: @primary;
    }
  }
}

.admins-toolbar,
.drivers-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  &__tip {
    font-size: 13px;
    color: rgba(16, 27, 37, 0.45);
  }

  &__actions {
    display: flex;
    gap: 8px;
  }
}

.admins-creator {
  font-size: 12px;
  color: rgba(16, 27, 37, 0.45);
}

.org-delete-tip {
  font-size: 13px;
  color: rgba(16, 27, 37, 0.65);
  margin-bottom: 16px;
}

.org-delete-name {
  color: #ff4d4f;
  font-weight: 600;
}

.org-join-preview {
  font-size: 13px;
  margin-top: 12px;
  color: @primary;

  &--muted {
    color: rgba(16, 27, 37, 0.45);
  }

  &--error {
    color: #ff4d4f;
  }
}

.org-add-admin-tip {
  font-size: 13px;
  color: rgba(16, 27, 37, 0.65);
  margin-bottom: 16px;
}

// 账户名实时检测提示（录入/占用/可用）
.account-hint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.5;

  &--checking {
    color: rgba(16, 27, 37, 0.45);
  }

  &--available {
    color: var(--pass-ink, #237804);
  }

  &--taken,
  &--invalid,
  &--email {
    color: var(--fail-ink, #a8071a);
  }
}
</style>
